import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { io } from 'socket.io-client';
import { useQuery } from '@tanstack/react-query';
import ImageWithLoader2 from './components/ImageWithLoader2';
import { formatDistanceToNow, parseISO } from 'date-fns';

const COLORS = {
  dark: '#75B437',
  accent: '#75B437',
  light: '#EBF4ED',
};

const APP_HEADER_H  = 64;  // global top bar
const CHAT_HEADER_H = 56;  
const COMPOSER_H    = 60;  
const FOOTER_H      = 56;  

const isLocalhost = window.location.hostname === 'localhost';
const SOCKET_URL = isLocalhost ? 'http://localhost:8081' : 'https://oret.africa';

const socket = io(SOCKET_URL, {
  path: '/tree/mobile/backend/socket.io',
  transports: ['polling', 'websocket'],
  withCredentials: true,
  autoConnect: false,
});

function Chats() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const location = useLocation();
  const receiverId = String(location.state?.receiverId ?? paramId ?? '');

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [userId, setUserId] = useState(null);
  const [isReceiverOnline, setIsReceiverOnline] = useState(false);
  const [isSameFamily, setIsSameFamily] = useState(true);
  const [isLoadingMsgs, setIsLoadingMsgs] = useState(true);

  const messageEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);


  const [pressedMsgId, setPressedMsgId] = useState(null);
  const pressTimerRef = useRef(null);
  const autoHideTimerRef = useRef(null);

  
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  const HiddenKey = useMemo(
    () => (userId && receiverId ? `chat:hidden:${userId}:${receiverId}` : null),
    [userId, receiverId]
  );

  useEffect(() => {
    if (!HiddenKey) return;
    try {
      const raw = localStorage.getItem(HiddenKey);
      if (raw) setHiddenIds(new Set(JSON.parse(raw)));
      else setHiddenIds(new Set());
    } catch {
      setHiddenIds(new Set());
    }
  }, [HiddenKey]);

  const persistHidden = (nextSet) => {
    if (!HiddenKey) return;
    try {
      localStorage.setItem(HiddenKey, JSON.stringify(Array.from(nextSet)));
    } catch {}
  };

  const hideMessageForMe = (id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(id);
      persistHidden(next);
      return next;
    });
    setMessages(prev => prev.filter(m => m.id !== id));
  };
 
  const chatWindowHeight = useMemo(
    () => `calc(100vh - ${APP_HEADER_H + CHAT_HEADER_H + FOOTER_H + 8}px)`,
    []
  );
  const scrollBottomPad = COMPOSER_H + 12;


  useEffect(() => {
    const id = localStorage.getItem('user_id');
    if (id) setUserId(String(id));
  }, []);

  // receiver 
  const { data: receiverInfo } = useQuery({
    queryKey: ['receiver-info', receiverId],
    queryFn: async () => (await axios.get(`${API_URL}/chat-member-receiver/${receiverId}`)).data,
    enabled: !!receiverId,
    staleTime: 60_000,
  });

  // initial messages
  useEffect(() => {
    let cancelled = false;
    if (!receiverId) return;
    setIsLoadingMsgs(true);
    axios
      .get(`${API_URL}/chat-messages/${receiverId}`)
      .then((res) => { if (!cancelled) setMessages(res.data || []); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setIsLoadingMsgs(false); });
    return () => { cancelled = true; };
  }, [receiverId]);

  
  useEffect(() => {
    if (!receiverId) return;
    axios
      .get(`${API_URL}/chat-box-color-api/${receiverId}`)
      .then((res) => setIsSameFamily(Boolean(res.data?.isSameFamily)))
      .catch(console.error);
  }, [receiverId]);


  useEffect(() => {
    if (!userId || !receiverId) return;

    socket.connect();
    socket.emit('join', userId);
    socket.emit('check_online_status', receiverId);

    const handleMessage = async (data) => {
      setMessages((prev) => [...prev, data]);
      if (String(data.sender_id) === String(receiverId)) {
        const token = localStorage.getItem('token');
        try {
          await axios.put(`${API_URL}/mark-as-read/${receiverId}`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) { console.error('Failed to mark as read (real-time):', err); }
      }
    };

    const handleOnlineStatus = ({ userId: onlineUserId, isOnline }) => {
      if (String(onlineUserId) === String(receiverId)) setIsReceiverOnline(Boolean(isOnline));
    };

    const handleDeleted = ({ id }) => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (pressedMsgId === id) setPressedMsgId(null);
    };

    socket.on('receive_message', handleMessage);
    socket.on('user_online_status', handleOnlineStatus);
    socket.on('message_deleted', handleDeleted);

    return () => {
      socket.off('receive_message', handleMessage);
      socket.off('user_online_status', handleOnlineStatus);
      socket.off('message_deleted', handleDeleted);
      socket.disconnect();
    };
  }, [userId, receiverId, pressedMsgId]);


  useEffect(() => {
    if (!receiverId) return;
    const token = localStorage.getItem('token');
    axios
      .put(`${API_URL}/mark-as-read/${receiverId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .catch((err) => console.error('Failed to mark as read:', err));
  }, [receiverId]);

  // scroll to bottom
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // send
  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !userId || !receiverId) return;

    const messageData = { sender_id: userId, receiver_id: receiverId, message: text };
    socket.emit('send_message', messageData);

    try {
      const res = await axios.post(`${API_URL}/send-message`, messageData);
      const savedMessage = res?.data?.message ?? messageData;
      setMessages((prev) => [...prev, savedMessage]);
      setDraft('');
      inputRef.current?.focus();
    } catch (err) { console.error('Failed to send message:', err); }
  };

  // delete (for everyone) — your existing server delete
  const deleteMessage = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/delete-message/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { receiver_id: receiverId },
      });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setPressedMsgId(null);
    } catch (err) { console.error('Failed to delete message:', err); }
  };

  // header text
  const headerStatusText = useMemo(() => {
    if (isReceiverOnline) return 'Online';
    const last = receiverInfo?.last_login;
    if (!last) return 'Offline';
    try { return `Last seen ${formatDistanceToNow(parseISO(last), { addSuffix: true })}`; }
    catch { return 'Offline'; }
  }, [isReceiverOnline, receiverInfo?.last_login]);

  // backgrounds with family tree pattern
  const bgStyle = useMemo(() => {
    // Change opacity to 0.05 for much lighter doodles
    const familyTreePattern = `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" opacity="0.05"><circle cx="100" cy="40" r="15" fill="%23184A2C"/><circle cx="60" cy="100" r="12" fill="%2375B437"/><circle cx="100" cy="100" r="12" fill="%2375B437"/><circle cx="140" cy="100" r="12" fill="%2375B437"/><line x1="100" y1="55" x2="100" y2="88" stroke="%23184A2C" stroke-width="2"/><line x1="100" y1="100" x2="60" y2="100" stroke="%23184A2C" stroke-width="2"/><line x1="100" y1="100" x2="140" y2="100" stroke="%23184A2C" stroke-width="2"/><circle cx="40" cy="160" r="10" fill="%2375B437"/><circle cx="80" cy="160" r="10" fill="%2375B437"/><circle cx="120" cy="160" r="10" fill="%2375B437"/><circle cx="160" cy="160" r="10" fill="%2375B437"/><line x1="60" y1="112" x2="60" y2="150" stroke="%23184A2C" stroke-width="1.5"/><line x1="140" y1="112" x2="140" y2="150" stroke="%23184A2C" stroke-width="1.5"/><line x1="60" y1="150" x2="40" y2="150" stroke="%23184A2C" stroke-width="1.5"/><line x1="60" y1="150" x2="80" y2="150" stroke="%23184A2C" stroke-width="1.5"/><line x1="140" y1="150" x2="120" y2="150" stroke="%23184A2C" stroke-width="1.5"/><line x1="140" y1="150" x2="160" y2="150" stroke="%23184A2C" stroke-width="1.5"/></svg>')`;
    
    return isSameFamily
      ? { 
          background: `${familyTreePattern}, linear-gradient(180deg, ${COLORS.light} 0%, #FFFFFF 100%)` 
        }
      : { 
          background: `${familyTreePattern}, linear-gradient(180deg, #FFF4E4 0%, #FFF9F0 100%)` 
        };
  }, [isSameFamily]);

  // long-press handlers (now for both sides)
  const startPressTimer = (msgId) => {
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      setPressedMsgId(msgId);
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = setTimeout(() => setPressedMsgId(null), 5000);
    }, 400);
  };
  const clearPressTimer = () => clearTimeout(pressTimerRef.current);
  const hideActions = () => {
    setPressedMsgId(null);
    clearTimeout(autoHideTimerRef.current);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="scrollPageContainer theme-light">
      <div className="main-wrapper" style={{ scrollPaddingTop: APP_HEADER_H + 64 }}>
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />

        <div className="page-wrapper" style={{ background: '#f5f7f8' }}>
          <div className="content container-fluid" style={{ paddingBottom: 0 }}>


            <div
              className="row"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1020,
                background: COLORS.light,
                padding: '8px 0',
                borderRadius: 12,
                boxShadow: '0 6px 24px rgba(0,0,0,0.06)',
                marginBottom: 10,
              }}
            >
              <div className="col-12 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    aria-label="Back"
                    title="Back"
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: 'none',
                      background: COLORS.light,
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M15 6L9 12L15 18" stroke={COLORS.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  {receiverInfo ? (
                    <>
                      <div style={{ position: 'relative' }}>
                        <ImageWithLoader2
                          src={receiverInfo.image_url}
                          placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                          fadeDuration={800}
                          blurAmount={15}
                          alt={`${receiverInfo.name} ${receiverInfo.last_name}`}
                          style={{
                            width: 44, height: 44, borderRadius: '50%', objectFit: 'cover',
                            border: `2px solid ${COLORS.light}`,
                          }}
                          onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                        />
                        <span
                          title={isReceiverOnline ? 'Online' : 'Offline'}
                          style={{
                            position: 'absolute', right: -2, bottom: -2, width: 12, height: 12,
                            borderRadius: '50%', background: isReceiverOnline ? COLORS.accent : '#9ca3af',
                            border: '2px solid #fff',
                          }}
                        />
                      </div>

                      <div className="d-flex flex-column">
                        <strong style={{ lineHeight: 1.2, color: '#184A2C' }}>
                          {receiverInfo?.n_prefix} {receiverInfo?.name} {receiverInfo?.last_name}
                        </strong>
                        <small style={{ color: isReceiverOnline ? COLORS.accent : '#6b7280' }}>

                          {isReceiverOnline
      ? "Online"
      : receiverInfo.last_login
      ? `Last seen ${formatDistanceToNow(parseISO(receiverInfo.last_login), {
          addSuffix: true,
        })}`
      : "Offline"}

                        </small>
                      </div>
                    </>
                  ) : (
                    <div className="placeholder-glow"><span className="placeholder col-6"></span></div>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">{/* future actions */}</div>
              </div>
            </div>

         
            <div className="row">
              <div className="col-12">
                <div
                  className="chat-window"
                  style={{
                    ...bgStyle,
                    border: `0px solid ${COLORS.light}`,
                    borderRadius: 16,
                    height: chatWindowHeight,
                    minHeight: 300,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  <div
                    className="chat-messages"
                    style={{
                      padding: `8px 2px ${scrollBottomPad}px`,
                      overflowY: 'auto',
                      flex: 1,
                      scrollBehavior: 'smooth',
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        hideActions();
                      }
                      inputRef.current?.focus();
                    }}
                  >
                    {isLoadingMsgs && <div className="text-center text-muted my-3 small">Loading messages…</div>}
                    {!isLoadingMsgs && messages.length === 0 && (
                      <div className="text-center text-muted my-3 small">No messages yet — say hi 👋</div>
                    )}

                    {messages
                      .filter((m) => !hiddenIds.has(m.id))
                      .map((msg) => {
                        const isMine = String(msg.sender_id) === String(userId);

                        return (
                          <div
                            key={msg.id ?? `${msg.sender_id}-${msg.created_at}-${Math.random()}`}
                            className={`d-flex mb-3 ${isMine ? 'justify-content-end' : 'justify-content-start'}`}
                          >
                            <div
                              className="position-relative"
                              style={{
                                maxWidth: '82%',
                                background: isMine 
                                  ? 'linear-gradient(135deg, #75B437 0%, #184A2C 100%)' 
                                  : '#ffffff',
                                color: isMine ? '#ffffff' : '#111827',
                                border: isMine 
                                  ? 'none'
                                  : `1px solid ${COLORS.light}`,
                                padding: '10px 12px',
                                borderRadius: 16,
                                borderTopRightRadius: isMine ? 6 : 16,
                                borderTopLeftRadius: isMine ? 16 : 6,
                                boxShadow: isMine 
                                  ? '0 4px 12px rgba(117, 180, 55, 0.3)'
                                  : '0 6px 20px rgba(0,0,0,0.06)',
                                wordBreak: 'break-word',
                                touchAction: 'manipulation',
                              }}
                              onTouchStart={() => startPressTimer(msg.id)}
                              onTouchEnd={clearPressTimer}
                              onTouchMove={clearPressTimer}
                              onMouseDown={() => startPressTimer(msg.id)}
                              onMouseUp={clearPressTimer}
                              onMouseLeave={clearPressTimer}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setPressedMsgId(msg.id);
                              }}
                            >
                              <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>

                              <div className="d-flex align-items-center mt-1" style={{ gap: 8, opacity: 0.95 }}>
                                <small style={{ color: isMine ? 'rgba(255, 255, 255, 0.9)' : 'inherit' }}>
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>

                           
                                {pressedMsgId === msg.id && (
                                  isMine ? (
                                 
                                    <button
                                      type="button"
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        e.preventDefault();
                                        deleteMessage(msg.id); 
                                      }}
                                      title="Delete for everyone"
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        padding: '2px 10px',
                                        borderRadius: 999,
                                        border: '1px solid white',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                      }}
                                    >
                                      🗑
                                    </button>
                                  ) : (
                                
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        hideMessageForMe(msg.id);
                                      }}
                                      title="Delete for me"
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        padding: '2px 10px',
                                        borderRadius: 999,
                                        border: '1px solid #ccc',
                                        background: 'rgba(0,0,0,0.06)',
                                        color: '#111827',
                                      }}
                                    >
                                      🗑
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    <div ref={messageEndRef} />
                  </div>
                </div>
              </div>
            </div>

      
            <div
              className="chat-input"
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: FOOTER_H + 19,
                background: 'transparent',
                border: 'none',
                zIndex: 1050,
                pointerEvents: 'none',
              }}
            >
              <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 12px', pointerEvents: 'auto' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: COLORS.light,
                    border: `1px solid ${COLORS.light}`,
                    borderRadius: 999,
                    padding: '4px 10px',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                  }}
                >
               
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach"
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#ffffff',
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 8.5L12 17.5C9.5 20 6 20 4 18C2 16 2 12.5 4.5 10L13.5 1H14" stroke={COLORS.dark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 3L7 12C6 13 6 15 7.5 16.5C9 18 11 18 12 17L20.5 8.5" stroke={COLORS.dark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  {/* Textarea */}
                  <textarea
                    ref={inputRef}
                    rows={1}
                    className="flex-grow-1"
                    placeholder="Type a message..."
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      const el = e.target;
                      el.style.height = 'auto';
                      el.style.height = Math.min(el.scrollHeight, 140) + 'px';
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      setTimeout(() => {
                        messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }, 250);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      padding: '8px 6px',
                      width: '100%',
                      maxHeight: 100,
                      color: COLORS.dark,
                    }}
                  />

                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!draft.trim()}
                    title="Send"
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: 'none',
                      cursor: draft.trim() ? 'pointer' : 'not-allowed',
                      background: draft.trim() ? COLORS.dark : '#e6ece8',
                      transition: 'transform .06s ease',
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13" stroke={draft.trim() ? '#ffffff' : '#9aa8a0'} strokeWidth="2" strokeLinecap="round" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={draft.trim() ? '#ffffff' : '#9aa8a0'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chats;
